import { createTheme } from '@mui/material/styles';

/**
 * AppealBot MUI theme.
 *
 * Palette and typography are sourced from CSS custom properties defined in
 * `frontend/src/styles/tokens.css` (the single source of truth).
 *
 * At module load, we read each --ab-* variable from `:root` via
 * `getComputedStyle(document.documentElement)` and feed it into MUI's theme.
 * For environments without a DOM (SSR, Jest jsdom before tokens.css is loaded),
 * each variable falls back to the literal hex from tokens.css so the theme
 * is still a valid object.
 *
 * If you need to change a color, edit tokens.css — NOT this file.
 */

const isBrowser = typeof document !== 'undefined' && document.documentElement;

function v(name, fallback) {
  if (!isBrowser) return fallback;
  const computed = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return computed || fallback;
}

const tokens = {
  fg:           v('--ab-fg',           '#000000'),
  fgMuted:      v('--ab-fg-muted',     '#1D2433'),
  fgSubtle:     v('--ab-fg-subtle',    '#64748B'),
  fgInverse:    v('--ab-fg-inverse',   '#FFFFFF'),
  bg:           v('--ab-bg',           '#FFFFFF'),
  bgAlt:        v('--ab-bg-alt',       '#F8F9FC'),
  bgHeader:     v('--ab-bg-header',    '#F1F3F9'),
  bgInput:      v('--ab-bg-input',     '#F7F8F9'),
  border:       v('--ab-border',       '#000000'),
  borderSoft:   v('--ab-border-soft',  '#A0ABBB'),
  denied:       v('--ab-denied',       '#FF0004'),
  approved:     v('--ab-approved',     '#1F8A4C'),
  pending:      v('--ab-pending',      '#B7791F'),
  info:         v('--ab-info',         '#1D4ED8'),
  fontSans:     v('--ab-font-sans',    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"),
  radiusSm:     v('--ab-radius-sm',    '4px'),
  radiusMd:     v('--ab-radius-md',    '8px'),
};

const appealbotTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.fg,
      contrastText: tokens.fgInverse,
    },
    secondary: {
      main: tokens.fgMuted,
      contrastText: tokens.fgInverse,
    },
    error: {
      main: tokens.denied,
      contrastText: tokens.fgInverse,
    },
    warning: {
      main: tokens.pending,
      contrastText: tokens.fgInverse,
    },
    success: {
      main: tokens.approved,
      contrastText: tokens.fgInverse,
    },
    info: {
      main: tokens.info,
      contrastText: tokens.fgInverse,
    },
    background: {
      default: tokens.bg,
      paper: tokens.bg,
    },
    text: {
      primary: tokens.fg,
      secondary: tokens.fgMuted,
      disabled: tokens.fgSubtle,
    },
    divider: tokens.border,
  },
  typography: {
    fontFamily: tokens.fontSans,
  },
  shape: {
    borderRadius: parseInt(tokens.radiusMd, 10) || 8,
  },
});

export default appealbotTheme;
